// ✅ FILE: src/components/Cookies/GoogleAnalyticsWrapper.jsx

import { useEffect, useState } from 'react';

const COOKIE_NAME = 'cookieConsent';
const GA_COOKIES = ['_ga', '_gid', '_gat'];

export default function GoogleAnalyticsWrapper({ gaId }) {
  const [consent, setConsent] = useState(null);

  useEffect(() => {
    console.log('[GA] Mounting GoogleAnalyticsWrapper');

    const updateConsentFromCookie = () => {
      const match = document.cookie.match(new RegExp(`${COOKIE_NAME}=([^;]+)`));
      if (match) {
        try {
          const parsed = JSON.parse(decodeURIComponent(match[1]));
          const changed = JSON.stringify(parsed) !== JSON.stringify(consent);
          if (changed) {
            console.log('[GA] Consent changed via polling:', parsed);
            setConsent(parsed);
          }
        } catch (err) {
          console.warn('[GA] Failed to parse cookieConsent:', err);
        }
      } else {
        console.log('[GA] No cookieConsent found');
      }
    };

    const interval = setInterval(updateConsentFromCookie, 2000);
    const stop = setTimeout(() => {
      clearInterval(interval);
      console.log('[GA] Polling stopped after 6s');
      if (consent && !consent.analytics) cleanupGA();
    }, 6000);

    return () => {
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, []);

  useEffect(() => {
    const channel = new BroadcastChannel('cookie-consent');
    channel.onmessage = (event) => {
      if (event.data?.type === 'consent-updated') {
        console.log('[GA] Received consent update broadcast');
        try {
          const match = document.cookie.match(new RegExp(`${COOKIE_NAME}=([^;]+)`));
          if (match) {
            const parsed = JSON.parse(decodeURIComponent(match[1]));
            setConsent(parsed);

            if (!parsed.analytics) {
              console.log('[GA] Consent revoked — disabling GA');
              cleanupGA();
            }
          }
        } catch (err) {
          console.warn('[GA] Failed to parse cookieConsent after broadcast update:', err);
        }
      }
    };
    return () => channel.close();
  }, []);

  useEffect(() => {
    if (consent?.analytics) {
      console.log('[GA] Consent granted — injecting GA');
      injectGA(gaId);
    } else {
      console.log('[GA] Consent not granted — GA will not be injected');
    }
  }, [consent?.analytics]);

  return null;
}

function injectGA(gaId) {
  if (document.getElementById('ga-script')) return;

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  script.async = true;
  script.id = 'ga-script';
  script.onload = () => console.log('[GA] gtag.js loaded');
  document.head.appendChild(script);

  const inlineScript = document.createElement('script');
  inlineScript.id = 'ga-init';
  inlineScript.innerHTML = `
    console.log('[GA] Initializing GA...');
    window.dataLayer = window.dataLayer || [];
    function gtag(){ dataLayer.push(arguments); }
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', '${gaId}', {
      anonymize_ip: true,
      page_path: window.location.pathname,
    });
  `;
  document.head.appendChild(inlineScript);
}

function cleanupGA() {
  GA_COOKIES.forEach((name) => {
    document.cookie = `${name}=; Max-Age=0; path=/`;
  });

  window.gtag = function () {
    console.warn('[GA] Blocked tracking call:', arguments);
  };

  const gaScript = document.getElementById('ga-script');
  if (gaScript) {
    gaScript.remove();
    console.log('[GA] Removed gtag.js');
  }

  const gaInit = document.getElementById('ga-init');
  if (gaInit) {
    gaInit.remove();
    console.log('[GA] Removed GA init script');
  }
}
